// src/pages/TranslationPage.tsx
import { useProjectValue } from "components/projects/ProjectDraft";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig";
import { useWebSocket } from "../providers/WebSocketProvider";
import { Spinner } from "react-bootstrap";
import { Card } from "components/ui/card";
import TranslationPageButtons from "../components/TranslationPageButtons";
import { ProgressStatus } from "../components/ProgressStatus";
import TranslationActionsPanel from "components/TranslationActionsPanel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { toast } from "hooks/use-toast";
import { mergeProgress, isTaskBusy } from "../utils/taskStatus";

//
// Interfaces
//
interface ITranslation {
  id: number;
  file_id: number;
  name?: string;
  translation_progress?: number;
  speech_generation_progress?: number;
  text_generation_progress?: number;
  target_language: string;
  audio_path: string | null;
  original_language?: string;
  voice_id?: string;
  job_status?: string | null;
  job_type?: string | null;
}

interface IPageResponse {
  original_text: string;
  translated_text: string;
  total_pages: number;
}

interface ICost {
  translation: string;
  voiceover: string;
}

const TranslationPage: React.FC = () => {
  const { id, projectId } = useParams<{ id: string; projectId: string }>();
  const navigate = useNavigate();

  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState<boolean>(true);
  const [translation, setTranslation] = useState<ITranslation | null>(null);

  const [currentPageNumber, setCurrentPageNumber] = useProjectValue<number>("pageNumber", 1);
  const resumePage = useRef(currentPageNumber);
  const [drafts, setDrafts] = useProjectValue<Record<string, string>>("textDrafts", {});
  const draftsRef = useRef(drafts); draftsRef.current = drafts;
  const [totalPages, setTotalPages] = useState<number>(1);

  const [translatedText, setTranslatedText] = useState<string>("");
  const [originalText, setOriginalText] = useState<string>("");
  const [originalTranslatedText, setOriginalTranslatedText] = useState<string>("");

  const [cost, setCost] = useState<ICost | null>(null);

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const [showOriginal, setShowOriginal] = useProjectValue<boolean>("showOriginal", false);

  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const pendingNavigationRef = useRef<null | (() => void)>(null);

  const currentPageRef = useRef(1);
  useEffect(() => {
    currentPageRef.current = currentPageNumber;
  }, [currentPageNumber]);

  /** WebSocket deltas before/without merged translation row (fixes missed events before fetch). */
  const [wsOverlay, setWsOverlay] = useState({ tr: 0, vo: 0, ts: 0 });
  useEffect(() => {
    setWsOverlay({ tr: 0, vo: 0, ts: 0 });
  }, [id]);

  const hasTranslatedText = !!translatedText.trim();

  const hasUnsavedChanges = isEditing && translatedText !== originalTranslatedText;

  const { eventBus } = useWebSocket();

  const fetchPageData = useCallback(
    async (translationId: number, pageNumber: number, opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoading(true);
      try {
        setLoadError("");
        const response = await axios.get<IPageResponse>(
          `/v2/translations/${translationId}/page/${pageNumber}`
        );
        const data = response.data;

        setOriginalText(data.original_text);
        const draft = draftsRef.current[String(pageNumber)];
        setTranslatedText(draft ?? data.translated_text);
        setIsEditing(draft !== undefined && draft !== data.translated_text);
        setOriginalTranslatedText(data.translated_text);
        setTotalPages(data.total_pages);
        setCurrentPageNumber(pageNumber);
      } catch (error) {
        setLoadError("This page could not be loaded. Reopen the step to try again.");
        console.error("Error while fetching translation page:", error);
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    []
  );

  const fetchCostData = async (fileId: number) => {
    try {
      const response = await axios.get<{
        translation_cost: { raw: string };
        voiceover_cost: { raw: string };
      }>(`/file/${fileId}/cost`);

      const { translation_cost, voiceover_cost } = response.data;
      setCost({
        translation: translation_cost.raw,
        voiceover: voiceover_cost.raw,
      });
    } catch (error) {
      console.error("Error while fetching cost data:", error);
    }
  };

  const fetchTranslation = useCallback(
    async (translationId: string) => {
      try {
        setLoading(true);
        const response = await axios.get<ITranslation>(`/v2/translation/${translationId}`);
        const data = response.data;

        if (!projectId && isTaskBusy(data, undefined, data.job_status ?? null)) {
          toast({
            title: "Task in progress",
            description: "Wait until processing finishes, then open from Tasks.",
          });
          navigate("/tasks");
          return;
        }

        setTranslation(data);
        await fetchPageData(data.id, resumePage.current);
        await fetchCostData(data.file_id);
      } catch (error) {
        setLoadError("This step could not be loaded. Reopen it to try again.");
        console.error("Error while fetching translation:", error);
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchPageData, projectId]
  );

  useEffect(() => {
    if (!id) return;
    fetchTranslation(id);
  }, [id, fetchTranslation]);

  useEffect(() => {
    if (!id) return;
    const tid = Number(id);
    if (Number.isNaN(tid)) return;

    const handleTranslationProgress = (data: { translation_id: number; progress: number }) => {
      if (Number(data.translation_id) !== tid) return;
      setWsOverlay((p) => ({ ...p, tr: mergeProgress(p.tr, data.progress) }));
      setTranslation((prev) =>
        prev
          ? {
              ...prev,
              translation_progress: mergeProgress(prev.translation_progress, data.progress),
            }
          : prev
      );
    };

    const handleVoiceoverProgress = (data: { translation_id: number; progress: number }) => {
      if (Number(data.translation_id) !== tid) return;
      setWsOverlay((p) => ({ ...p, vo: mergeProgress(p.vo, data.progress) }));
      setTranslation((prev) =>
        prev
          ? {
              ...prev,
              speech_generation_progress: mergeProgress(prev.speech_generation_progress, data.progress),
            }
          : prev
      );
    };

    const handleTranscriptProgress = (data: { translation_id: number; progress: number }) => {
      if (Number(data.translation_id) !== tid) return;
      setWsOverlay((p) => ({ ...p, ts: mergeProgress(p.ts, data.progress) }));
      setTranslation((prev) =>
        prev
          ? {
              ...prev,
              text_generation_progress: mergeProgress(prev.text_generation_progress, data.progress),
            }
          : prev
      );
      if (data.progress === 100) {
        const refresh = async () => {
          try {
            const res = await axios.get<ITranslation>(`/v2/translation/${tid}`);
            setTranslation(res.data);
            await fetchPageData(tid, currentPageRef.current, { silent: true });
          } catch (e) {
            console.error(e);
          }
        };
        void refresh();
        // finalize_transcription runs after the last chunk; first 100 can arrive before DB pages update.
        window.setTimeout(() => void refresh(), 2500);
      }
    };

    eventBus.on("tr_progress", handleTranslationProgress);
    eventBus.on("vo_progress", handleVoiceoverProgress);
    eventBus.on("ts_progress", handleTranscriptProgress);

    return () => {
      eventBus.off("tr_progress", handleTranslationProgress);
      eventBus.off("vo_progress", handleVoiceoverProgress);
      eventBus.off("ts_progress", handleTranscriptProgress);
    };
  }, [eventBus, id, fetchPageData]);

  const navigateToPage = useCallback(
    async (targetPage: number) => {
      if (!translation) return;

      pendingNavigationRef.current = null;

      if (hasUnsavedChanges) {
        pendingNavigationRef.current = () => {
          void fetchPageData(translation.id, targetPage);
        };

        setShowSaveDialog(true);
        return;
      }

      await fetchPageData(translation.id, targetPage);
    },
    [translation, hasUnsavedChanges, fetchPageData]
  );

  const handleNextPage = async () => {
    if (!translation) return;
    if (currentPageNumber < totalPages) {
      await navigateToPage(currentPageNumber + 1);
    }
  };

  const handlePreviousPage = async () => {
    if (!translation) return;
    if (currentPageNumber > 1) {
      await navigateToPage(currentPageNumber - 1);
    }
  };

  const handleGoToPage = async (page: number) => {
    if (!translation) return;
    await navigateToPage(page);
  };

  const handleToggleEdit = () => {
    if (isEditing && hasUnsavedChanges) {
      pendingNavigationRef.current = () => {
        setIsEditing(false);
      };
      setShowSaveDialog(true);
      return;
    }

    setIsEditing((prev) => !prev);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      handleDiscardChanges();
    }
    setShowSaveDialog(open);
  };

  const handleSaveCompleted = async () => {
    setDrafts(previous => {const next = {...previous}; delete next[String(currentPageNumber)]; return next;});
    setOriginalTranslatedText(translatedText);

    const pendingNavigation = pendingNavigationRef.current;

    setShowSaveDialog(false);

    if (pendingNavigation) {
      setTimeout(() => {
        pendingNavigation();
        pendingNavigationRef.current = null;
      }, 100);
    }
  };

  const handleDiscardChanges = () => {
    setDrafts(previous => {const next = {...previous}; delete next[String(currentPageNumber)]; return next;});
    setTranslatedText(originalTranslatedText);

    const pendingNavigation = pendingNavigationRef.current;

    pendingNavigationRef.current = null;
    setShowSaveDialog(false);

    if (pendingNavigation) {
      setTimeout(() => {
        pendingNavigation();
      }, 100);
    }
  };

  const updateDraftText = (text: string) => {
    setTranslatedText(text);
    setDrafts(previous => ({...previous, [String(currentPageNumber)]:text}));
  };

  const transcriptProgressDisplay = translation
    ? mergeProgress(translation.text_generation_progress, wsOverlay.ts)
    : mergeProgress(undefined, wsOverlay.ts);
  const translationProgressDisplay = translation
    ? mergeProgress(translation.translation_progress, wsOverlay.tr)
    : mergeProgress(undefined, wsOverlay.tr);
  const speechProgressDisplay = translation
    ? mergeProgress(translation.speech_generation_progress, wsOverlay.vo)
    : mergeProgress(undefined, wsOverlay.vo);

  return (
    <div className="min-h-screen flex flex-col pt-3 pb-6 px-4 sm:px-6 md:px-8">
      <AlertDialog open={showSaveDialog} onOpenChange={handleDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save them before continuing?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-center sm:justify-center gap-4">
            <AlertDialogCancel onClick={handleDiscardChanges} className="mt-2">
              Don't Save
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => document.getElementById("saveButton")?.click()}
              className="mt-2"
            >
              Save Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {loadError && <div className="p-4 mb-4 rounded-lg bg-red-50 text-red-700" role="alert">{loadError}</div>}
      <div className="mb-6">
        {loading && <Spinner animation="border" variant="primary" />}

        {!loading && translation && (
          <>
            <h1 className="text-2xl font-semibold text-center mb-4">
              {translation.name} ({translation.target_language})
            </h1>

            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-1/2">
                <ProgressStatus
                  transcriptProgress={transcriptProgressDisplay}
                  translationProgress={translationProgressDisplay}
                  speechProgress={speechProgressDisplay}
                />
              </div>
            </div>

            <TranslationPageButtons
              translation={translation}
              cost={cost}
              editorState={{
                isEditing,
                onToggleEdit: handleToggleEdit,
                translatedText,
                onChangeTranslatedText: updateDraftText,
                showOriginal,
                setShowOriginal,
                hasTranslatedText,
                onSave: handleSaveCompleted,
              }}
              pageNavigation={{
                currentPageNumber,
                totalPages,
                onNextPage: handleNextPage,
                onPreviousPage: handlePreviousPage,
                goToPage: handleGoToPage,
              }}
            />
          </>
        )}
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {!hasTranslatedText && (
          <Card className="p-6 h-full overflow-auto">
            <div className="whitespace-pre-wrap text-base">{originalText}</div>
          </Card>
        )}

        {hasTranslatedText && showOriginal && (
          <div className="grid grid-cols-2 gap-4 flex-1 h-full h-min-[70vh]">
            <Card className="p-6 overflow-auto h-full flex flex-col">
              <h3 className="text-md font-medium mb-3 text-muted-foreground">Original</h3>
              <div className="whitespace-pre-wrap text-base overflow-auto flex-1 pb-4">{originalText}</div>
            </Card>
            <Card className="p-6 overflow-auto h-full flex flex-col">
              <h3 className="text-md font-medium mb-3 text-muted-foreground">Translated</h3>
              <textarea
                className="w-full flex-1 resize-none bg-transparent outline-none border-none overflow-auto p-0"
                placeholder="Translated text"
                disabled={!isEditing}
                value={translatedText}
                onChange={(e) => updateDraftText(e.target.value)}
              />
            </Card>
          </div>
        )}

        {hasTranslatedText && !showOriginal && (
          <Card className="p-6 overflow-auto flex flex-col flex-1">
            <textarea
              className="w-full flex-1 resize-none bg-transparent outline-none border-none overflow-auto"
              placeholder="Translated text"
              disabled={!isEditing}
              value={translatedText}
              onChange={(e) => updateDraftText(e.target.value)}
            />
          </Card>
        )}
      </div>

      {translation && <TranslationActionsPanel translation={translation} cost={cost} />}
    </div>
  );
};

export default TranslationPage;
